import type { CellClassParams, CellStyle, ColDef, ITooltipParams } from '@ag-grid-community/core';
import { getQuantityCellValidationState } from '../deal-detail/deal-reconfirm.utils';
import { MaturityOrder, QuantityCellValidationState } from './deal-series.types';
import { allowOnlyWholePositiveNumbers } from './deal-series.util';
import { SplitOrderRow } from './split-order-modal/split-order-modal.types';

const dealSeriesBorder = '1px solid #505050';

const lightGreyCell = {
  backgroundColor: 'rgba(135, 155, 166, 0.06)',
  borderBottom: dealSeriesBorder,
  height: '40px',
};

const orderEntryCellStyle = {
  ...lightGreyCell,
  border: dealSeriesBorder,
  boxSizing: 'border-box',
};

const warningOrderEntryCellStyle = {
  ...orderEntryCellStyle,
  border: '1px solid #ffb660',
};

/** ag-grid slotted styles for quantity-error / quantity-warning cells (maturity grid + submit/cancel modals). */
export const quantityValidationGridSlottedStyles = `
  .ag-cell.quantity-error {
    border: 1px solid var(--error-color, #ff4444) !important;
    box-sizing: border-box !important;
  }

  .ag-cell.quantity-error:hover {
    border: 1px solid var(--error-color, #ff4444) !important;
    box-sizing: border-box !important;
  }

  .ag-cell.quantity-warning {
    border: 1px solid #ffb660 !important;
    box-sizing: border-box !important;
  }

  .ag-cell.quantity-warning:hover {
    border: 1px solid #ffb660 !important;
    box-sizing: border-box !important;
  }
`;

export interface QuantityColumnValidationConfig {
  dealStatus?: string | null;
  /** Prefer over dealStatus when status can change without rebuilding column defs. */
  getDealStatus?: () => string | null | undefined;
  pinnedRowCellStyle?: CellStyle | ((params: { node: { isRowPinned?: () => boolean } }) => CellStyle);
  /** When set, validates this order instead of params.data (e.g. split modal row.order + splitAmount). */
  getValidationOrder?: (params: CellClassParams) => MaturityOrder | undefined;
  /** When true, skip validation styling (e.g. source/total row in split modal). */
  skipValidation?: (params: CellClassParams) => boolean;
  /** When true, apply validation border/classes on pinned rows (split modal parent total). */
  includePinnedRowsInValidation?: boolean;
}

/** Applies quantity validation CSS classes on an ag-grid cell without refreshing the renderer. */
export const applyQuantityValidationCellClasses = (
  cellElement: Element | null | undefined,
  state: QuantityCellValidationState,
): void => {
  if (!(cellElement instanceof HTMLElement)) return;
  cellElement.classList.toggle('quantity-error', state.hasError);
  cellElement.classList.toggle('quantity-warning', !state.hasError && state.hasWarning);
};

/** Maps a split grid row to the maturity order shape used by quantity validation. */
export const getSplitRowValidationOrder = (
  row: SplitOrderRow | undefined,
): MaturityOrder | undefined => {
  if (!row || row.rowType === 'source') return undefined;
  const quantity = String(row.splitAmount ?? '').trim();
  if (!quantity) {
    return { ...row.order, QUANTITY: '' };
  }
  return { ...row.order, QUANTITY: allowOnlyWholePositiveNumbers(quantity) || quantity };
};

/** Shared quantity column validation display (cell style, classes, tooltips) for maturity and modal grids. */
export const getQuantityColumnValidationColDef = (
  config: QuantityColumnValidationConfig = {},
): Pick<ColDef, 'cellStyle' | 'cellClassRules' | 'tooltipValueGetter'> => {
  const { pinnedRowCellStyle, includePinnedRowsInValidation = false } = config;
  const resolveDealStatus = (): string | null | undefined =>
    config.getDealStatus?.() ?? config.dealStatus;

  const shouldValidateRow = (params: CellClassParams | ITooltipParams): boolean => {
    if (config.skipValidation?.(params as CellClassParams)) return false;
    if (params.node?.isRowPinned?.() && !includePinnedRowsInValidation) return false;
    return true;
  };

  const validationState = (params: CellClassParams | ITooltipParams): QuantityCellValidationState => {
    if (!shouldValidateRow(params)) {
      return { hasError: false, hasWarning: false, tooltip: null };
    }
    const order =
      config.getValidationOrder?.(params as CellClassParams) ??
      (params.data as MaturityOrder | undefined);
    return order
      ? getQuantityCellValidationState(order, resolveDealStatus())
      : { hasError: false, hasWarning: false, tooltip: null };
  };

  return {
    cellStyle: (params) => {
      if (params.node?.isRowPinned?.()) {
        if (includePinnedRowsInValidation) {
          const state = validationState(params);
          if (state.hasError) {
            return { ...orderEntryCellStyle, textAlign: 'right' };
          }
          if (state.hasWarning) {
            return { ...warningOrderEntryCellStyle, textAlign: 'right' };
          }
        }
        if (typeof pinnedRowCellStyle === 'function') {
          return pinnedRowCellStyle(params);
        }
        return pinnedRowCellStyle ?? { textAlign: 'right' };
      }
      const state = validationState(params);
      if (state.hasError) return orderEntryCellStyle;
      if (state.hasWarning) return warningOrderEntryCellStyle;
      return orderEntryCellStyle;
    },
    cellClassRules: {
      'quantity-error': (params: CellClassParams) =>
        shouldValidateRow(params) && validationState(params).hasError,
      'quantity-warning': (params: CellClassParams) =>
        shouldValidateRow(params) && validationState(params).hasWarning,
    },
    tooltipValueGetter: (params: ITooltipParams) =>
      shouldValidateRow(params) ? validationState(params).tooltip : null,
  };
};
